import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const FilterPanel = ({ isOpen, onToggle, onFiltersChange, currentFilters = {} }) => {
  const [filters, setFilters] = useState({
    dateRange: currentFilters?.dateRange || 'last_30_days',
    trafficSource: currentFilters?.trafficSource || 'all',
    buyerSegment: currentFilters?.buyerSegment || 'all',
    publisherSegment: currentFilters?.publisherSegment || 'all',
    revenueRange: currentFilters?.revenueRange || { min: '', max: '' },
    geography: currentFilters?.geography || 'all',
    customMetrics: currentFilters?.customMetrics || []
  });

  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'last_90_days', label: 'Last 90 Days' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const trafficSourceOptions = [
    { value: 'all', label: 'All Traffic Sources' },
    { value: 'social', label: 'Social Media' },
    { value: 'tv', label: 'Television' },
    { value: 'radio', label: 'Radio' },
    { value: 'direct_mail', label: 'Direct Mail' },
    { value: 'search', label: 'Search Engine' },
    { value: 'video', label: 'Video Advertising' },
    { value: 'organic', label: 'Organic' }
  ];

  const buyerSegmentOptions = [
    { value: 'all', label: 'All Buyers' },
    { value: 'enterprise', label: 'Enterprise (1000+ employees)' },
    { value: 'mid_market', label: 'Mid-Market (100-999 employees)' },
    { value: 'small_business', label: 'Small Business (10-99 employees)' },
    { value: 'individual_agents', label: 'Individual Agents' },
    { value: 'high_volume', label: 'High Volume Buyers' },
    { value: 'premium_tier', label: 'Premium Tier' }
  ];

  const publisherSegmentOptions = [
    { value: 'all', label: 'All Publishers' },
    { value: 'top_performers', label: 'Top Performers (Top 10%)' },
    { value: 'high_volume', label: 'High Volume (500+ calls/month)' },
    { value: 'medium_volume', label: 'Medium Volume (100-499 calls/month)' },
    { value: 'new_publishers', label: 'New Publishers (< 90 days)' },
    { value: 'exclusive_partners', label: 'Exclusive Partners' }
  ];

  const geographyOptions = [
    { value: 'all', label: 'All Regions' },
    { value: 'northeast', label: 'Northeast' },
    { value: 'southeast', label: 'Southeast' },
    { value: 'midwest', label: 'Midwest' },
    { value: 'southwest', label: 'Southwest' },
    { value: 'west', label: 'West Coast' },
    { value: 'california', label: 'California' },
    { value: 'texas', label: 'Texas' },
    { value: 'florida', label: 'Florida' },
    { value: 'new_york', label: 'New York' }
  ];

  const customMetricOptions = [
    { value: 'conversion_rate', label: 'Conversion Rate' },
    { value: 'call_duration', label: 'Average Call Duration' },
    { value: 'lead_quality_score', label: 'Lead Quality Score' },
    { value: 'time_to_conversion', label: 'Time to Conversion' },
    { value: 'customer_lifetime_value', label: 'Customer Lifetime Value' },
    { value: 'cost_per_acquisition', label: 'Cost Per Acquisition' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    if (onFiltersChange) {
      onFiltersChange(newFilters);
    }
  };

  const handleRevenueRangeChange = (type, value) => {
    const newRevenueRange = { ...filters?.revenueRange, [type]: value };
    handleFilterChange('revenueRange', newRevenueRange);
  };

  const resetFilters = () => {
    const defaultFilters = {
      dateRange: 'last_30_days',
      trafficSource: 'all',
      buyerSegment: 'all',
      publisherSegment: 'all',
      revenueRange: { min: '', max: '' },
      geography: 'all',
      customMetrics: []
    };
    setFilters(defaultFilters);
    if (onFiltersChange) {
      onFiltersChange(defaultFilters);
    }
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.dateRange !== 'last_30_days') count++;
    if (filters?.trafficSource !== 'all') count++;
    if (filters?.buyerSegment !== 'all') count++;
    if (filters?.publisherSegment !== 'all') count++;
    if (filters?.revenueRange?.min || filters?.revenueRange?.max) count++;
    if (filters?.geography !== 'all') count++;
    if (filters?.customMetrics?.length > 0) count++;
    return count;
  };

  return (
    <div className={`bg-card border border-border rounded-lg shadow-card transition-all duration-300 ${
      isOpen ? 'max-h-screen opacity-100' : 'max-h-16 opacity-100'
    }`}>
      {/* Filter Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Filter" size={20} className="text-primary" />
          <h3 className="font-semibold text-foreground">Advanced Filters</h3>
          {getActiveFilterCount() > 0 && (
            <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
              {getActiveFilterCount()} active
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {getActiveFilterCount() > 0 && (
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              Reset
            </Button>
          )}
          <button
            onClick={onToggle}
            className="p-2 text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name={isOpen ? "ChevronUp" : "ChevronDown"} size={16} />
          </button>
        </div>
      </div>
      {/* Filter Content */}
      {isOpen && (
        <div className="p-4 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Date Range */}
            <Select
              label="Date Range"
              options={dateRangeOptions}
              value={filters?.dateRange}
              onChange={(value) => handleFilterChange('dateRange', value)}
            />

            {/* Traffic Source */}
            <Select
              label="Traffic Source"
              options={trafficSourceOptions}
              value={filters?.trafficSource}
              onChange={(value) => handleFilterChange('trafficSource', value)}
            />

            {/* Geography */}
            <Select
              label="Geography"
              options={geographyOptions}
              value={filters?.geography}
              onChange={(value) => handleFilterChange('geography', value)}
            />

            {/* Buyer Segment */}
            <Select
              label="Buyer Segment"
              options={buyerSegmentOptions}
              value={filters?.buyerSegment}
              onChange={(value) => handleFilterChange('buyerSegment', value)}
            />

            {/* Publisher Segment */}
            <Select
              label="Publisher Segment"
              options={publisherSegmentOptions}
              value={filters?.publisherSegment}
              onChange={(value) => handleFilterChange('publisherSegment', value)}
            />

            {/* Custom Metrics */}
            <Select
              label="Additional Metrics"
              options={customMetricOptions}
              value={filters?.customMetrics}
              onChange={(value) => handleFilterChange('customMetrics', value)}
              multiple
              searchable
              placeholder="Select metrics to include"
            />
          </div>

          {/* Revenue Range */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Revenue Range</label>
            <div className="grid grid-cols-2 gap-4">
              <Input
                type="number"
                placeholder="Min ($)"
                value={filters?.revenueRange?.min}
                onChange={(e) => handleRevenueRangeChange('min', e?.target?.value)}
              />
              <Input
                type="number"
                placeholder="Max ($)"
                value={filters?.revenueRange?.max}
                onChange={(e) => handleRevenueRangeChange('max', e?.target?.value)}
              />
            </div>
          </div>

          {/* Quick Filter Presets */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Quick Presets</label>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  handleFilterChange('buyerSegment', 'high_volume');
                  handleFilterChange('dateRange', 'last_7_days');
                }}
              >
                High Volume This Week
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  handleFilterChange('publisherSegment', 'top_performers');
                  handleFilterChange('dateRange', 'last_30_days');
                }}
              >
                Top Publishers
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  handleFilterChange('trafficSource', 'social');
                  handleFilterChange('dateRange', 'this_month');
                }}
              >
                Social Media Performance
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;